import { useEffect, useState } from "react";
import { track, fileUrl } from "@/lib/api";
import { useBooking } from "@/context/BookingContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { CalendarCheck, Maximize2 } from "lucide-react";

const STAGES = [
  {
    period: "Il dopoguerra",
    title: "Le origini: l'osteria di Dina e Guerrino",
    image: "/api/files/masetti/uploads/c99a325b-778d-41c6-bbb2-e52730eb55df.jpg",
    imageAlt: "Fotografia d'epoca: un uomo con una bambina in braccio sulla soglia dell'osteria",
    caption: "Sulla soglia dell'osteria, in una fotografia di famiglia",
    wide: true,
    paras: [
      "A Gesso, sulle colline di Zola Predosa, l'osteria di Dina e Guerrino Masetti divenne nel dopoguerra un importante luogo di ritrovo per la comunità. Intorno alle sue tavole si incontravano persone, si condividevano racconti e si coltivava quel senso di convivialità che ancora oggi appartiene alla nostra famiglia.",
    ],
  },
  {
    period: "1961",
    title: "Dall'osteria al Ristorante Masetti",
    image: "/api/files/masetti/uploads/6d838ad7-7a09-4519-8a0a-3ab8bfb7d65f.jpg",
    imageAlt: "Il Ristorante Masetti a Gesso visto dall'alto, fotografia storica",
    caption: "Il ristorante visto dall'alto, in una fotografia d'epoca",
    wide: true,
    paras: [
      "Nel 1961, dopo la ristrutturazione, l'osteria diventò il Ristorante Masetti. La guida era ormai passata a Gianni, Marisa e Franca, che proseguirono il percorso iniziato da Dina e Guerrino. Una nuova veste per il locale, con la stessa attenzione all'accoglienza e un legame profondo con la cucina bolognese.",
    ],
  },
  {
    period: "Verso la fine del Novecento",
    title: "Una stagione di grande successo",
    image: "/api/files/masetti/uploads/4b095a37-0654-4a51-9a90-23646915c7d0.jpg",
    imageAlt: "Premio Internazionale Ercole d'Oro, Oscar per il turismo e la ristorazione, assegnato a Gianni Masetti · Roma, 1988",
    caption: "Premio Internazionale Ercole d'Oro assegnato a Gianni Masetti · Roma, 1988",
    paras: [
      "Verso la fine del Novecento, Masetti conobbe una stagione di grande crescita. Il ristorante accolse grandi aziende e ospiti del cinema, della musica, del teatro e dello sport. Nel corso della sua storia, dalle sue tavole sono passati Roberto Benigni, Cesare Cremonini, Brigitte Nielsen, Beppe Grillo, Giovanni Rana e Michael Ray Richardson.",
      "Fotografie e ricordi custodiscono la memoria di questi incontri, insieme a quella dei tanti clienti che hanno scelto Masetti per celebrare una ricorrenza, ritrovarsi in famiglia o condividere un'occasione speciale.",
    ],
  },
  {
    period: "Oggi",
    title: "La tradizione incontra una nuova generazione",
    image: "/api/files/masetti/uploads/3f3fdb29-ac67-4642-8f98-52e46e622bfa.jpg",
    imageAlt: "La sala del Ristorante Masetti oggi, con i tavoli apparecchiati e la luce naturale dalle vetrate",
    caption: "La sala del ristorante oggi",
    wide: true,
    paras: [
      "Oggi le nuove generazioni affiancano Gianni, Marisa e Franca, dando continuità alla storia della famiglia e portando nuove idee. La pasta fresca fatta in casa, le crescentine e la carne ai ferri restano al centro di una cucina che conserva le proprie radici bolognesi.",
      "Con la cura di sempre e una rinnovata energia, lavoriamo per far tornare Masetti a brillare sulla scena bolognese, continuando a offrire ciò che da generazioni dà significato al nostro lavoro: il piacere di accogliervi e farvi stare bene a tavola.",
    ],
  },
];

export default function Storia() {
  const { open: openBooking } = useBooking();
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    track("page_view", "storia");
  }, []);

  return (
    <div>
      <SEO title="La Nostra Storia" description="La storia del Ristorante Masetti a Zola Predosa: dall'osteria di Dina e Guerrino al Ristorante Masetti, una famiglia e la cucina bolognese dal 1961." />

      {/* HEADER */}
      <div className="bg-oak-dark text-cream py-16 sm:py-20 text-center px-4">
        <h1 className="font-serif text-4xl sm:text-5xl">La nostra storia</h1>
        <p className="font-accent italic text-brass text-lg mt-3">Generazioni di famiglia, accoglienza e cucina bolognese.</p>
      </div>

      {/* TIMELINE */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="relative">
          {/* Linea verticale: a sinistra su mobile, al centro su desktop */}
          <div className="absolute top-2 bottom-2 w-px bg-brass/30 left-[7px] md:left-1/2 md:-translate-x-1/2" aria-hidden="true" />

          <div className="space-y-16 md:space-y-24">
            {STAGES.map((s, i) => {
              const left = i % 2 === 0;
              return (
                <div key={s.period} className="relative md:grid md:grid-cols-2 md:items-start md:gap-x-16" data-testid={`storia-stage-${i}`}>
                  {/* Indicatore */}
                  <span className="absolute top-1.5 w-4 h-4 rounded-full bg-bordeaux ring-4 ring-cream left-0 md:left-1/2 md:-translate-x-1/2" aria-hidden="true" />

                  <div className={`pl-10 md:pl-0 ${left ? "md:col-start-1 md:text-right md:pr-16" : "md:col-start-2 md:pl-16"}`}>
                    <Reveal direction={left ? "left" : "right"}>
                      <p className="font-serif text-2xl sm:text-3xl text-brass">{s.period}</p>
                      <h2 className="font-serif text-xl sm:text-2xl text-bordeaux mt-1.5">{s.title}</h2>
                      <div className="mt-3 space-y-3">
                        {s.paras.map((p, j) => (
                          <p key={j} className="text-oak/75 leading-relaxed">{p}</p>
                        ))}
                      </div>
                    </Reveal>
                  </div>
                  {s.image && (
                    <div className={`pl-10 md:pl-0 mt-6 md:mt-0 ${left ? "md:col-start-2 md:pl-16" : "md:col-start-1 md:row-start-1 md:pr-16"}`}>
                      <Reveal direction={left ? "right" : "left"}>
                        <figure className={`${s.wide ? "" : "max-w-xs"} mx-auto md:mx-0`} data-testid={`storia-stage-${i}-image`}>
                          <button type="button" onClick={() => setZoom(s)} className="group relative block w-full rounded-xl overflow-hidden border border-brass/25 shadow-md cursor-zoom-in" aria-label={`Ingrandisci: ${s.imageAlt}`} data-testid={`storia-stage-${i}-zoom`}>
                            <img src={fileUrl(s.image)} alt={s.imageAlt} loading="lazy" className="w-full transition-transform duration-500 group-hover:scale-[1.03]" />
                            <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-oak-dark/70 text-cream text-[11px] px-2.5 py-1 backdrop-blur-sm"><Maximize2 className="w-3 h-3" />Ingrandisci</span>
                          </button>
                          {s.caption && <figcaption className="font-accent italic text-sm text-oak/60 mt-2 text-center">{s.caption}</figcaption>}
                        </figure>
                      </Reveal>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CHIUSURA */}
      <section className="bg-cream-dark border-t border-brass/20 py-16 text-center px-4">
        <p className="font-accent italic text-xl sm:text-2xl text-oak max-w-2xl mx-auto">Una storia che continua, una tavola che vi aspetta.</p>
        <Button onClick={() => openBooking("storia")} className="bg-bordeaux hover:bg-bordeaux-dark text-cream rounded-full px-8 h-12 mt-6" data-testid="storia-cta-prenota">
          <CalendarCheck className="w-4 h-4 mr-2" />Prenota un tavolo
        </Button>
      </section>

      <Dialog open={!!zoom} onOpenChange={(o) => !o && setZoom(null)}>
        <DialogContent className="max-w-[96vw] sm:max-w-4xl p-2 sm:p-3 bg-oak-dark border-brass/30" data-testid="storia-image-dialog">
          {zoom && (
            <figure>
              <DialogTitle className="sr-only">{zoom.imageAlt}</DialogTitle>
              <img src={fileUrl(zoom.image)} alt={zoom.imageAlt} className="w-full max-h-[80vh] object-contain rounded-lg" />
              {zoom.caption && <figcaption className="font-accent italic text-cream/80 text-sm text-center mt-2 px-2">{zoom.caption}</figcaption>}
            </figure>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
